import { useState, useEffect, useCallback, useRef } from 'react';
import { Plus, Trash2, Edit2, ToggleLeft, ToggleRight, BarChart2, ChevronDown, ChevronUp, Check, Image, Video, Link as LinkIcon, Monitor, Smartphone, Upload, X, AlertCircle, CheckCircle, RefreshCw } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const CHUNK_SIZE = 512 * 1024; // 512 KB per chunk

const EMPTY_BANNER = {
  title: '',
  subtitle: '',
  image_url: '',
  mobile_image_url: '',
  video_url: '',
  cta_text: '',
  cta_link: '',
  sponsor_name: '',
  sponsor_logo_url: '',
  active: true,
  start_date: '',
  end_date: '',
};

const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const ALLOWED_EXTS = ['.jpg', '.jpeg', '.png', '.webp'];
const LOCAL_PATH_RE = /^([a-zA-Z]:\\|file:\/\/|blob:|data:)/i;

// ── Chunked upload helper ─────────────────────────────────────────────────────
async function uploadImageInChunks(file, imageType, onProgress) {
  const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
  const uploadId = `upload_${Date.now()}_${Math.random().toString(36).slice(2)}`;
  let lastResult = null;

  for (let i = 0; i < totalChunks; i++) {
    const start = i * CHUNK_SIZE;
    const chunk = file.slice(start, start + CHUNK_SIZE);
    const fd = new FormData();
    fd.append('file', new File([chunk], file.name, { type: file.type }));
    fd.append('upload_id', uploadId);
    fd.append('chunk_index', String(i));
    fd.append('total_chunks', String(totalChunks));
    fd.append('image_type', imageType);

    const res = await fetch(`${API}/admin/spotlight/upload-image`, {
      method: 'POST',
      credentials: 'include',
      body: fd,
    });

    if (!res.ok) {
      const err = await res.json().catch(() => ({ detail: 'Upload failed' }));
      throw new Error(err.detail || 'Upload failed');
    }

    lastResult = await res.json();
    onProgress(Math.round(((i + 1) / totalChunks) * 100));
  }

  return lastResult; // { status:'complete', serve_path, img_id }
}

// ── ImageUploader component ───────────────────────────────────────────────────
function ImageUploader({ label, aspectLabel, recommendedSize, value, onChange, imageType }) {
  const inputRef = useRef(null);
  const [progress, setProgress] = useState(0);   // 0–100
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [filename, setFilename] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [externalUrl, setExternalUrl] = useState('');
  // blob URL for instant local preview before upload completes
  const [blobUrl, setBlobUrl] = useState('');

  // Derive display URL: blob (uploading) → uploaded permanent URL → empty
  const displayUrl = uploading ? blobUrl : (value || '');

  const handleFile = async (file) => {
    setError('');
    // Type check
    if (!ALLOWED_TYPES.includes(file.type)) {
      setError(`Unsupported file type. Please use JPG, PNG, or WebP.`);
      return;
    }
    // Size check
    if (file.size > 10 * 1024 * 1024) {
      setError(`File too large (${(file.size / 1024 / 1024).toFixed(1)} MB). Max 10 MB.`);
      return;
    }

    // Instant local preview
    const blob = URL.createObjectURL(file);
    setBlobUrl(blob);
    setFilename(file.name);
    setUploading(true);
    setProgress(0);

    try {
      const result = await uploadImageInChunks(file, imageType, setProgress);
      const permanentUrl = `${process.env.REACT_APP_BACKEND_URL}${result.serve_path}`;
      onChange(permanentUrl);
      setProgress(100);
    } catch (e) {
      setError(e.message || 'Upload failed. Please try again.');
      onChange('');
      setBlobUrl('');
    } finally {
      setUploading(false);
      // Keep blob URL alive briefly then revoke
      setTimeout(() => URL.revokeObjectURL(blob), 5000);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  };

  const handleExternalUrl = () => {
    const url = externalUrl.trim();
    if (!url) return;
    if (LOCAL_PATH_RE.test(url)) {
      setError('This is a local file path. Please use the Upload Image button.');
      return;
    }
    if (!url.startsWith('https://')) {
      setError('Only HTTPS URLs are accepted.');
      return;
    }
    setError('');
    setFilename('');
    onChange(url);
    setShowAdvanced(false);
  };

  const handleRemove = () => {
    onChange('');
    setFilename('');
    setBlobUrl('');
    setError('');
    setProgress(0);
    if (inputRef.current) inputRef.current.value = '';
  };

  return (
    <div className="space-y-2" data-testid={`image-uploader-${imageType}`}>
      <div className="flex items-center justify-between">
        <label className="text-xs font-semibold text-[#2B2B2B]/60 uppercase tracking-wider flex items-center gap-1.5">
          <Monitor size={11} className={imageType === 'mobile' ? 'hidden' : ''} />
          <Smartphone size={11} className={imageType !== 'mobile' ? 'hidden' : ''} />
          {label}
          <span className="text-[#2B2B2B]/30 font-normal normal-case tracking-normal ml-1">{aspectLabel}</span>
        </label>
        {value && !uploading && (
          <button
            type="button"
            onClick={handleRemove}
            className="text-xs text-red-400 hover:text-red-600 flex items-center gap-1 transition-colors"
            data-testid={`remove-image-${imageType}`}
          >
            <X size={11} /> Remove
          </button>
        )}
      </div>

      {/* Drop zone / upload button */}
      {!value && !uploading && (
        <div
          onDrop={handleDrop}
          onDragOver={e => e.preventDefault()}
          className="relative border-2 border-dashed border-[#C8A97E]/30 rounded-xl bg-[#F6F1E9]/60 hover:border-[#C8A97E]/60 hover:bg-[#F6F1E9] transition-all"
        >
          <input
            ref={inputRef}
            type="file"
            accept={ALLOWED_EXTS.join(',')}
            onChange={e => e.target.files[0] && handleFile(e.target.files[0])}
            className="absolute inset-0 opacity-0 cursor-pointer"
            data-testid={`file-input-${imageType}`}
          />
          <div className="flex flex-col items-center justify-center gap-2 py-5 px-4 pointer-events-none">
            <div className="w-9 h-9 rounded-xl bg-[#C8A97E]/12 flex items-center justify-center">
              <Upload size={17} className="text-[#C8A97E]" />
            </div>
            <div className="text-center">
              <p className="text-xs font-semibold text-[#2B2B2B]">
                Upload {label.replace('Upload ', '')}
              </p>
              <p className="text-[10px] text-[#2B2B2B]/40 mt-0.5">
                {recommendedSize} · JPG, PNG, WebP · max 10 MB
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Upload progress */}
      {uploading && (
        <div className="border border-[#C8A97E]/20 rounded-xl p-3 bg-[#F6F1E9]/60 space-y-2" data-testid={`upload-progress-${imageType}`}>
          <div className="flex items-center justify-between text-xs">
            <span className="font-semibold text-[#2B2B2B] truncate max-w-[200px]">{filename}</span>
            <span className="text-[#C8A97E] font-bold">{progress}%</span>
          </div>
          <div className="h-1.5 bg-[#2B2B2B]/8 rounded-full overflow-hidden">
            <div
              className="h-full bg-[#C8A97E] rounded-full transition-all duration-200"
              style={{ width: `${progress}%` }}
            />
          </div>
          {blobUrl && (
            <img src={blobUrl} alt="preview" className="w-full h-16 object-cover rounded-lg mt-1" />
          )}
        </div>
      )}

      {/* Uploaded — show thumbnail + filename */}
      {value && !uploading && (
        <div className="flex items-center gap-3 border border-[#8A9A8B]/15 rounded-xl p-2.5 bg-white" data-testid={`uploaded-image-${imageType}`}>
          <img
            src={value}
            alt="Uploaded"
            className="w-14 h-10 object-cover rounded-lg flex-shrink-0 bg-[#2B2B2B]/5"
          />
          <div className="flex-1 min-w-0">
            <p className="text-xs font-semibold text-[#2B2B2B] truncate">{filename || 'Uploaded image'}</p>
            <p className="text-[10px] text-[#8A9A8B] mt-0.5 flex items-center gap-1">
              <CheckCircle size={9} className="text-green-500" /> Saved & ready
            </p>
          </div>
          <label className="cursor-pointer flex items-center gap-1.5 text-xs font-semibold text-[#C8A97E] hover:text-[#b8956a] transition-colors flex-shrink-0">
            <RefreshCw size={11} /> Replace
            <input
              ref={inputRef}
              type="file"
              accept={ALLOWED_EXTS.join(',')}
              onChange={e => e.target.files[0] && handleFile(e.target.files[0])}
              className="hidden"
            />
          </label>
        </div>
      )}

      {/* Error message */}
      {error && (
        <div className="flex items-start gap-2 text-xs text-red-600 bg-red-50 border border-red-200 rounded-xl px-3 py-2" data-testid={`upload-error-${imageType}`}>
          <AlertCircle size={13} className="flex-shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      {/* Advanced: external URL */}
      <button
        type="button"
        onClick={() => setShowAdvanced(s => !s)}
        className="text-[10px] text-[#2B2B2B]/35 hover:text-[#2B2B2B]/55 transition-colors flex items-center gap-1"
        data-testid={`toggle-advanced-${imageType}`}
      >
        <LinkIcon size={9} /> {showAdvanced ? 'Hide' : 'Use external image URL instead'}
      </button>

      {showAdvanced && (
        <div className="space-y-1.5" data-testid={`advanced-url-${imageType}`}>
          <div className="flex gap-2">
            <input
              type="url"
              value={externalUrl}
              onChange={e => {
                setExternalUrl(e.target.value);
                setError('');
              }}
              placeholder="https://... (HTTPS only)"
              className="flex-1 border border-[#8A9A8B]/20 rounded-xl px-3 py-2 text-xs text-[#2B2B2B] bg-[#F6F1E9] focus:outline-none focus:border-[#C8A97E]"
            />
            <button
              type="button"
              onClick={handleExternalUrl}
              className="px-3 py-2 text-xs font-semibold bg-[#2B2B2B] text-white rounded-xl hover:bg-[#3a3a3a] transition-colors"
            >
              Use
            </button>
          </div>
          <p className="text-[10px] text-[#2B2B2B]/35">Only publicly accessible HTTPS URLs. Local paths are rejected.</p>
        </div>
      )}
    </div>
  );
}

// ── Live preview panel ────────────────────────────────────────────────────────
function ImagePreview({ desktopUrl, mobileUrl, videoUrl }) {
  const [view, setView] = useState('desktop');
  const [imgError, setImgError] = useState(false);
  const activeUrl = view === 'mobile' ? (mobileUrl || desktopUrl) : desktopUrl;
  useEffect(() => setImgError(false), [activeUrl]);

  const hasMedia = desktopUrl || mobileUrl || videoUrl;
  if (!hasMedia) return null;

  return (
    <div className="sm:col-span-2" data-testid="banner-image-preview">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-semibold text-[#2B2B2B]/50 uppercase tracking-wider">Preview</span>
        <div className="flex items-center gap-0.5 bg-[#F6F1E9] border border-[#8A9A8B]/20 rounded-lg p-0.5">
          <button type="button" onClick={() => setView('desktop')} data-testid="preview-desktop-btn"
            className={`flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-semibold transition-all ${view === 'desktop' ? 'bg-white text-[#2B2B2B] shadow-sm' : 'text-[#2B2B2B]/40 hover:text-[#2B2B2B]'}`}>
            <Monitor size={11} /> Desktop
          </button>
          <button type="button" onClick={() => setView('mobile')} data-testid="preview-mobile-btn"
            className={`flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-semibold transition-all ${view === 'mobile' ? 'bg-white text-[#2B2B2B] shadow-sm' : 'text-[#2B2B2B]/40 hover:text-[#2B2B2B]'}`}>
            <Smartphone size={11} /> Mobile
          </button>
        </div>
      </div>

      {view === 'desktop' ? (
        <div className="relative w-full overflow-hidden rounded-xl bg-[#1a1a1a]" style={{ aspectRatio: '16/5' }}>
          {videoUrl ? (
            <video src={videoUrl} autoPlay muted loop playsInline className="absolute inset-0 w-full h-full object-cover" />
          ) : desktopUrl && !imgError ? (
            <img src={desktopUrl} alt="Desktop preview" className="absolute inset-0 w-full h-full object-cover" onError={() => setImgError(true)} />
          ) : (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-white/25">
              <Image size={28} />
              <span className="text-xs">{imgError ? 'Image failed to load' : 'No desktop image'}</span>
            </div>
          )}
          <div className="absolute inset-0" style={{ background: 'linear-gradient(to right, rgba(0,0,0,0.55) 0%, transparent 60%)' }} />
          <div className="absolute inset-0 flex items-center px-5">
            <div style={{ background: 'rgba(246,241,233,0.08)', backdropFilter: 'blur(12px)', border: '1px solid rgba(200,169,126,0.3)', borderRadius: '12px', padding: '10px 14px' }}>
              <div style={{ width: '20px', height: '2px', background: '#C8A97E', borderRadius: '2px', marginBottom: '6px' }} />
              <p className="text-white font-bold text-xs" style={{ fontFamily: 'Georgia,serif' }}>Banner preview</p>
              <p className="text-white/50 text-[10px] mt-0.5">16:9 landscape</p>
            </div>
          </div>
          <div className="absolute bottom-1.5 right-2 text-white/25 text-[10px] font-semibold tracking-wider">DESKTOP</div>
        </div>
      ) : (
        <div className="flex flex-col items-center gap-3">
          <div className="relative" style={{ width: '130px' }}>
            <div className="relative rounded-[20px] overflow-hidden border-[3px] border-[#2B2B2B]/20 bg-[#1a1a1a]" style={{ aspectRatio: '9/16' }}>
              {videoUrl ? (
                <video src={videoUrl} autoPlay muted loop playsInline className="absolute inset-0 w-full h-full object-cover" />
              ) : (mobileUrl || desktopUrl) && !imgError ? (
                <img src={mobileUrl || desktopUrl} alt="Mobile preview" className="absolute inset-0 w-full h-full object-cover" onError={() => setImgError(true)} />
              ) : (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-1 text-white/25">
                  <Smartphone size={20} />
                  <span className="text-[9px]">No image</span>
                </div>
              )}
              <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.55) 0%, transparent 50%)' }} />
              {mobileUrl ? (
                <div className="absolute top-2 left-1 right-1 bg-green-600/85 text-white text-[8px] font-bold rounded-md px-1.5 py-0.5 text-center">
                  Mobile image set
                </div>
              ) : desktopUrl ? (
                <div className="absolute top-2 left-1 right-1 bg-amber-500/85 text-white text-[8px] font-bold rounded-md px-1.5 py-0.5 text-center leading-tight">
                  Using desktop image
                </div>
              ) : null}
            </div>
            <div className="absolute top-2 left-1/2 -translate-x-1/2 w-7 h-1.5 bg-[#2B2B2B]/25 rounded-full" />
          </div>
          {!mobileUrl && desktopUrl && (
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2 text-center max-w-xs">
              No mobile image — desktop image used on phones. Upload a 9:16 portrait image for best results.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

// ── Banner form ───────────────────────────────────────────────────────────────
function BannerForm({ initial, onSave, onCancel, saving }) {
  const [form, setForm] = useState(initial || EMPTY_BANNER);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="sm:col-span-2">
          <label className="text-xs font-semibold text-[#2B2B2B]/60 uppercase tracking-wider">Title *</label>
          <input value={form.title} onChange={e => set('title', e.target.value)} placeholder="Athens Spotlight"
            data-testid="spotlight-title-input"
            className="mt-1 w-full border border-[#8A9A8B]/20 rounded-xl px-3 py-2 text-sm text-[#2B2B2B] bg-[#F6F1E9] focus:outline-none focus:border-[#C8A97E]" />
        </div>
        <div className="sm:col-span-2">
          <label className="text-xs font-semibold text-[#2B2B2B]/60 uppercase tracking-wider">Subtitle</label>
          <input value={form.subtitle} onChange={e => set('subtitle', e.target.value)} placeholder="Short description shown on the banner"
            className="mt-1 w-full border border-[#8A9A8B]/20 rounded-xl px-3 py-2 text-sm text-[#2B2B2B] bg-[#F6F1E9] focus:outline-none focus:border-[#C8A97E]" />
        </div>

        {/* Desktop image upload */}
        <div className="sm:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <ImageUploader
            label="Desktop Image"
            aspectLabel="16:9 · 1920×1080 recommended"
            recommendedSize="1920×1080px"
            value={form.image_url}
            onChange={v => set('image_url', v)}
            imageType="desktop"
          />
          <ImageUploader
            label="Mobile Image"
            aspectLabel="9:16 · 1080×1920 recommended"
            recommendedSize="1080×1920px"
            value={form.mobile_image_url}
            onChange={v => set('mobile_image_url', v)}
            imageType="mobile"
          />
        </div>

        {/* Live preview */}
        <ImagePreview desktopUrl={form.image_url} mobileUrl={form.mobile_image_url} videoUrl={form.video_url} />

        <div className="sm:col-span-2">
          <label className="text-xs font-semibold text-[#2B2B2B]/60 uppercase tracking-wider flex items-center gap-1">
            <Video size={11} /> Video URL <span className="text-[#2B2B2B]/30 font-normal normal-case tracking-normal text-xs ml-1">optional — overrides image</span>
          </label>
          <input value={form.video_url} onChange={e => set('video_url', e.target.value)} placeholder="https://... .mp4"
            className="mt-1 w-full border border-[#8A9A8B]/20 rounded-xl px-3 py-2 text-sm text-[#2B2B2B] bg-[#F6F1E9] focus:outline-none focus:border-[#C8A97E]" />
        </div>

        <div>
          <label className="text-xs font-semibold text-[#2B2B2B]/60 uppercase tracking-wider flex items-center gap-1"><LinkIcon size={11} /> CTA Button Text</label>
          <input value={form.cta_text} onChange={e => set('cta_text', e.target.value)} placeholder="Explore Deals"
            className="mt-1 w-full border border-[#8A9A8B]/20 rounded-xl px-3 py-2 text-sm text-[#2B2B2B] bg-[#F6F1E9] focus:outline-none focus:border-[#C8A97E]" />
        </div>
        <div>
          <label className="text-xs font-semibold text-[#2B2B2B]/60 uppercase tracking-wider">CTA Link</label>
          <input value={form.cta_link} onChange={e => set('cta_link', e.target.value)} placeholder="https://... or /explore"
            className="mt-1 w-full border border-[#8A9A8B]/20 rounded-xl px-3 py-2 text-sm text-[#2B2B2B] bg-[#F6F1E9] focus:outline-none focus:border-[#C8A97E]" />
        </div>

        {/* Sponsor */}
        <div>
          <label className="text-xs font-semibold text-[#2B2B2B]/60 uppercase tracking-wider">Sponsor Name (optional)</label>
          <input value={form.sponsor_name} onChange={e => set('sponsor_name', e.target.value)} placeholder="Brand name"
            className="mt-1 w-full border border-[#8A9A8B]/20 rounded-xl px-3 py-2 text-sm text-[#2B2B2B] bg-[#F6F1E9] focus:outline-none focus:border-[#C8A97E]" />
        </div>
        <div>
          <label className="text-xs font-semibold text-[#2B2B2B]/60 uppercase tracking-wider">Sponsor Logo URL</label>
          <div className="mt-1 flex items-center gap-2">
            <input value={form.sponsor_logo_url} onChange={e => set('sponsor_logo_url', e.target.value)} placeholder="https://..."
              data-testid="spotlight-sponsor-logo-input"
              className="flex-1 border border-[#8A9A8B]/20 rounded-xl px-3 py-2 text-sm text-[#2B2B2B] bg-[#F6F1E9] focus:outline-none focus:border-[#C8A97E]" />
            {form.sponsor_logo_url && (
              <div className="w-10 h-10 rounded-xl border border-[#C8A97E]/20 bg-[#2B2B2B] flex items-center justify-center flex-shrink-0 overflow-hidden" data-testid="sponsor-logo-preview">
                <img src={form.sponsor_logo_url} alt="Logo" className="w-full h-full object-contain p-1" onError={e => { e.currentTarget.style.display = 'none'; }} />
              </div>
            )}
          </div>
          {form.sponsor_name && form.sponsor_logo_url && (
            <div className="mt-2 flex items-center gap-2 bg-[#2B2B2B]/5 rounded-lg px-3 py-2" data-testid="sponsor-badge-preview">
              <img src={form.sponsor_logo_url} alt={form.sponsor_name} className="h-4 object-contain" onError={e => { e.currentTarget.style.display = 'none'; }} />
              <span className="text-xs font-semibold" style={{ color: '#C8A97E', letterSpacing: '0.12em', textTransform: 'uppercase' }}>
                Sponsored by {form.sponsor_name}
              </span>
            </div>
          )}
        </div>

        <div>
          <label className="text-xs font-semibold text-[#2B2B2B]/60 uppercase tracking-wider">Start Date (optional)</label>
          <input type="date" value={form.start_date} onChange={e => set('start_date', e.target.value)}
            className="mt-1 w-full border border-[#8A9A8B]/20 rounded-xl px-3 py-2 text-sm text-[#2B2B2B] bg-[#F6F1E9] focus:outline-none focus:border-[#C8A97E]" />
        </div>
        <div>
          <label className="text-xs font-semibold text-[#2B2B2B]/60 uppercase tracking-wider">End Date (optional)</label>
          <input type="date" value={form.end_date} onChange={e => set('end_date', e.target.value)}
            className="mt-1 w-full border border-[#8A9A8B]/20 rounded-xl px-3 py-2 text-sm text-[#2B2B2B] bg-[#F6F1E9] focus:outline-none focus:border-[#C8A97E]" />
        </div>

        <div className="sm:col-span-2 flex items-center gap-3">
          <button type="button" onClick={() => set('active', !form.active)} data-testid="spotlight-active-toggle"
            className="flex items-center gap-2 text-sm font-semibold">
            {form.active ? <ToggleRight size={22} className="text-[#C8A97E]" /> : <ToggleLeft size={22} className="text-[#2B2B2B]/30" />}
            <span className={form.active ? 'text-[#2B2B2B]' : 'text-[#2B2B2B]/40'}>Banner Active</span>
          </button>
        </div>
      </div>

      <div className="flex gap-3 pt-2">
        <button onClick={() => onSave(form)} disabled={saving || !form.title.trim()} data-testid="spotlight-save-banner-btn"
          className="btn-primary text-sm px-5 py-2.5 flex items-center gap-2">
          <Check size={14} /> {saving ? 'Saving…' : 'Save Banner'}
        </button>
        <button onClick={onCancel} className="px-5 py-2.5 text-sm font-semibold text-[#2B2B2B]/60 border border-[#8A9A8B]/20 rounded-full hover:border-[#C8A97E] transition-colors">
          Cancel
        </button>
      </div>
    </div>
  );
}

// ── Banner card (list item) ───────────────────────────────────────────────────
function BannerCard({ banner, onEdit, onDelete, onToggleActive, index, total, onMoveUp, onMoveDown }) {
  const [showStats, setShowStats] = useState(false);
  const stats = banner.stats || {};

  return (
    <div data-testid={`spotlight-banner-card-${banner.banner_id}`}
      className="bg-white border border-[#8A9A8B]/10 rounded-2xl overflow-hidden">
      <div className="flex items-start gap-4 p-4">
        <div className="w-20 h-14 rounded-xl overflow-hidden bg-[#2B2B2B]/5 flex-shrink-0 relative">
          {banner.image_url
            ? <img src={banner.image_url} alt={banner.title} className="w-full h-full object-cover" />
            : <div className="w-full h-full flex items-center justify-center text-[#2B2B2B]/20"><Image size={20} /></div>}
          {banner.video_url && (
            <div className="absolute inset-0 bg-black/30 flex items-center justify-center"><Video size={14} className="text-white" /></div>
          )}
          {banner.mobile_image_url && (
            <div className="absolute bottom-0.5 right-0.5 bg-[#C8A97E] rounded-sm p-0.5"><Smartphone size={8} className="text-white" /></div>
          )}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <p className="font-semibold text-sm text-[#2B2B2B] truncate">{banner.title}</p>
              {banner.subtitle && <p className="text-xs text-[#2B2B2B]/50 mt-0.5 line-clamp-1">{banner.subtitle}</p>}
              <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${banner.active ? 'bg-green-100 text-green-700' : 'bg-[#2B2B2B]/5 text-[#2B2B2B]/40'}`}>
                  {banner.active ? 'Active' : 'Inactive'}
                </span>
                {banner.mobile_image_url && (
                  <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-[#C8A97E]/15 text-[#C8A97E] flex items-center gap-1">
                    <Smartphone size={9} /> Mobile
                  </span>
                )}
                {banner.sponsor_name && (
                  <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-[#8A9A8B]/15 text-[#8A9A8B]">Sponsored</span>
                )}
                {(banner.start_date || banner.end_date) && (
                  <span className="text-xs text-[#2B2B2B]/40">{banner.start_date || '—'} → {banner.end_date || '—'}</span>
                )}
              </div>
            </div>

            <div className="flex items-center gap-1 flex-shrink-0">
              <button onClick={() => onMoveUp(index)} disabled={index === 0} aria-label="Move up"
                className="p-1.5 rounded-lg hover:bg-[#F6F1E9] disabled:opacity-30 transition-colors"><ChevronUp size={14} /></button>
              <button onClick={() => onMoveDown(index)} disabled={index === total - 1} aria-label="Move down"
                className="p-1.5 rounded-lg hover:bg-[#F6F1E9] disabled:opacity-30 transition-colors"><ChevronDown size={14} /></button>
              <button onClick={() => onToggleActive(banner.banner_id, !banner.active)} data-testid={`spotlight-toggle-${banner.banner_id}`}
                className="p-1.5 rounded-lg hover:bg-[#F6F1E9] transition-colors" title={banner.active ? 'Deactivate' : 'Activate'}>
                {banner.active ? <ToggleRight size={16} className="text-[#C8A97E]" /> : <ToggleLeft size={16} className="text-[#2B2B2B]/30" />}
              </button>
              <button onClick={() => onEdit(banner)} data-testid={`spotlight-edit-${banner.banner_id}`}
                className="p-1.5 rounded-lg hover:bg-[#F6F1E9] transition-colors" title="Edit">
                <Edit2 size={14} className="text-[#C8A97E]" />
              </button>
              <button onClick={() => setShowStats(s => !s)} className="p-1.5 rounded-lg hover:bg-[#F6F1E9] transition-colors" title="Analytics">
                <BarChart2 size={14} className="text-[#8A9A8B]" />
              </button>
              <button onClick={() => onDelete(banner.banner_id)} data-testid={`spotlight-delete-${banner.banner_id}`}
                className="p-1.5 rounded-lg hover:bg-red-50 transition-colors" title="Delete">
                <Trash2 size={14} className="text-red-400" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {showStats && (
        <div className="border-t border-[#8A9A8B]/10 px-4 py-3 bg-[#F6F1E9]/50">
          <div className="flex gap-6 text-xs">
            {[
              { label: 'Impressions', value: stats.impressions || 0 },
              { label: 'Clicks', value: stats.clicks || 0 },
              { label: 'CTR', value: stats.impressions ? `${((stats.clicks || 0) / stats.impressions * 100).toFixed(1)}%` : '—' },
              { label: 'Mobile', value: stats.mobile_impressions || 0 },
            ].map(({ label, value }) => (
              <div key={label}>
                <p className="text-[#2B2B2B]/40 font-semibold uppercase tracking-wider">{label}</p>
                <p className="text-[#2B2B2B] font-bold text-sm mt-0.5">{value}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main manager ──────────────────────────────────────────────────────────────
export default function AdminSpotlightManager({ showMsg }) {
  const [config, setConfig] = useState({ enabled: false, rotation_mode: 'auto', rotation_interval: 5 });
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingBanner, setEditingBanner] = useState(null);
  const [saving, setSaving] = useState(false);
  const [configSaving, setConfigSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cfgRes, banRes] = await Promise.all([
        fetch(`${API}/admin/spotlight/config`, { credentials: 'include' }),
        fetch(`${API}/admin/spotlight/banners`, { credentials: 'include' }),
      ]);
      const cfgData = await cfgRes.json();
      const banData = await banRes.json();
      setConfig(cfgData.config || {});
      setBanners(banData.banners || []);
    } catch {
      showMsg && showMsg('Failed to load Spotlight data');
    }
    setLoading(false);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const saveConfig = async (updates) => {
    setConfigSaving(true);
    try {
      const res = await fetch(`${API}/admin/spotlight/config`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates),
      });
      const d = await res.json();
      setConfig(d.config || {});
      showMsg && showMsg('Settings saved');
    } catch { showMsg && showMsg('Failed to save settings'); }
    setConfigSaving(false);
  };

  const handleToggleEnabled = () => {
    const next = !config.enabled;
    saveConfig({ ...config, enabled: next });
    setConfig(c => ({ ...c, enabled: next }));
  };

  const handleSaveBanner = async (form) => {
    setSaving(true);
    try {
      let res;
      if (editingBanner) {
        res = await fetch(`${API}/admin/spotlight/banners/${editingBanner.banner_id}`, {
          method: 'PATCH', credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(form),
        });
      } else {
        res = await fetch(`${API}/admin/spotlight/banners`, {
          method: 'POST', credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(form),
        });
      }
      if (res.ok) {
        showMsg && showMsg(editingBanner ? 'Banner updated' : 'Banner created');
        setShowForm(false);
        setEditingBanner(null);
        load();
      } else {
        const e = await res.json();
        showMsg && showMsg(e.detail || 'Failed to save banner');
      }
    } catch { showMsg && showMsg('Error saving banner'); }
    setSaving(false);
  };

  const handleDelete = async (bannerId) => {
    if (!window.confirm('Delete this banner?')) return;
    await fetch(`${API}/admin/spotlight/banners/${bannerId}`, { method: 'DELETE', credentials: 'include' });
    showMsg && showMsg('Banner deleted');
    load();
  };

  const handleToggleActive = async (bannerId, active) => {
    await fetch(`${API}/admin/spotlight/banners/${bannerId}`, {
      method: 'PATCH', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ active }),
    });
    setBanners(bs => bs.map(b => b.banner_id === bannerId ? { ...b, active } : b));
  };

  const handleMoveUp = async (index) => {
    if (index === 0) return;
    const newOrder = [...banners];
    [newOrder[index - 1], newOrder[index]] = [newOrder[index], newOrder[index - 1]];
    setBanners(newOrder);
    await fetch(`${API}/admin/spotlight/banners/reorder`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ordered_ids: newOrder.map(b => b.banner_id) }),
    });
  };

  const handleMoveDown = async (index) => {
    if (index === banners.length - 1) return;
    const newOrder = [...banners];
    [newOrder[index], newOrder[index + 1]] = [newOrder[index + 1], newOrder[index]];
    setBanners(newOrder);
    await fetch(`${API}/admin/spotlight/banners/reorder`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ordered_ids: newOrder.map(b => b.banner_id) }),
    });
  };

  if (loading) return <div className="text-center py-10 text-[#2B2B2B]/40 text-sm">Loading Spotlight…</div>;

  return (
    <div className="space-y-6" data-testid="admin-spotlight-manager">
      {/* Master toggle */}
      <div className="bg-white rounded-2xl border border-[#8A9A8B]/10 p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-bold text-[#2B2B2B] text-lg" style={{ fontFamily: 'Playfair Display, serif' }}>Perkly Spotlight</h3>
            <p className="text-xs text-[#2B2B2B]/50 mt-1">Full-width banner below the homepage hero. Hidden completely when disabled.</p>
          </div>
          <button onClick={handleToggleEnabled} disabled={configSaving} data-testid="spotlight-master-toggle"
            className="flex items-center gap-2 text-sm font-semibold">
            {config.enabled ? <ToggleRight size={28} className="text-[#C8A97E]" /> : <ToggleLeft size={28} className="text-[#2B2B2B]/25" />}
            <span className={config.enabled ? 'text-[#2B2B2B]' : 'text-[#2B2B2B]/40'}>{config.enabled ? 'Spotlight ON' : 'Spotlight OFF'}</span>
          </button>
        </div>
        <div className="flex flex-wrap gap-6 pt-4 border-t border-[#8A9A8B]/10">
          <div>
            <label className="text-xs font-semibold text-[#2B2B2B]/50 uppercase tracking-wider">Rotation Mode</label>
            <select value={config.rotation_mode || 'auto'} onChange={e => saveConfig({ ...config, rotation_mode: e.target.value })}
              data-testid="spotlight-rotation-mode"
              className="mt-1 border border-[#8A9A8B]/20 rounded-lg px-3 py-1.5 text-sm text-[#2B2B2B] bg-[#F6F1E9] focus:outline-none">
              <option value="auto">Auto (timed)</option>
              <option value="manual">Manual (arrows only)</option>
            </select>
          </div>
          {config.rotation_mode !== 'manual' && (
            <div>
              <label className="text-xs font-semibold text-[#2B2B2B]/50 uppercase tracking-wider">Interval (seconds)</label>
              <select value={config.rotation_interval || 5} onChange={e => saveConfig({ ...config, rotation_interval: Number(e.target.value) })}
                data-testid="spotlight-rotation-interval"
                className="mt-1 border border-[#8A9A8B]/20 rounded-lg px-3 py-1.5 text-sm text-[#2B2B2B] bg-[#F6F1E9] focus:outline-none">
                {[3, 5, 7, 10, 15].map(s => <option key={s} value={s}>{s}s</option>)}
              </select>
            </div>
          )}
        </div>
      </div>

      {/* Banner list */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h4 className="font-semibold text-[#2B2B2B] text-sm uppercase tracking-wider">Banners ({banners.length})</h4>
          <button onClick={() => { setEditingBanner(null); setShowForm(s => !s); }}
            data-testid="spotlight-add-banner-btn"
            className="btn-primary text-xs px-4 py-2 flex items-center gap-1.5">
            <Plus size={13} /> Add Banner
          </button>
        </div>

        {showForm && (
          <div className="bg-white border border-[#C8A97E]/25 rounded-2xl p-5 mb-4" data-testid="spotlight-banner-form">
            <h5 className="font-semibold text-[#2B2B2B] mb-4 text-sm">
              {editingBanner ? `Editing: ${editingBanner.title}` : 'New Banner'}
            </h5>
            <BannerForm
              initial={editingBanner}
              onSave={handleSaveBanner}
              onCancel={() => { setShowForm(false); setEditingBanner(null); }}
              saving={saving}
            />
          </div>
        )}

        {banners.length === 0 ? (
          <div className="text-center py-10 text-[#2B2B2B]/35 text-sm bg-white rounded-2xl border border-[#8A9A8B]/10">
            No banners yet. Add one above to get started.
          </div>
        ) : (
          <div className="space-y-3">
            {banners.map((banner, i) => (
              <BannerCard key={banner.banner_id} banner={banner} index={i} total={banners.length}
                onEdit={b => { setEditingBanner(b); setShowForm(true); }}
                onDelete={handleDelete} onToggleActive={handleToggleActive}
                onMoveUp={handleMoveUp} onMoveDown={handleMoveDown} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
